import { Send, Square } from 'lucide-react'
import { useEffect, useState, type KeyboardEvent } from 'react'
import { Capacitor } from '@capacitor/core'

interface ChatInputProps {
  onSend: (content: string) => void
  /** Whether a turn is currently streaming. Disables the textarea and swaps
   *  the Send button for a Stop button. */
  isStreaming: boolean
  /** Called when the user clicks Stop. Should abort the in-flight stream. */
  onStop?: () => void
  /** When set, the composer adopts this text as its current draft. */
  prefill?: string
  /** Called after a prefill has been consumed so parent can clear it. */
  onPrefillConsumed?: () => void
}

export function ChatInput({
  onSend,
  isStreaming,
  onStop,
  prefill,
  onPrefillConsumed,
}: ChatInputProps) {
  const [value, setValue] = useState('')
  const isNative = Capacitor.isNativePlatform()

  useEffect(() => {
    if (prefill) {
      setValue(prefill)
      onPrefillConsumed?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill])

  const submit = () => {
    const trimmed = value.trim()
    if (!trimmed || isStreaming) return
    onSend(trimmed)
    setValue('')
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Note: the textarea is `disabled` while streaming, so this handler
    // doesn't actually fire in that case — the visible Stop button is the
    // only way to cancel. Keeping the guard for safety in case the
    // textarea ever becomes non-disabled while streaming.
    if (isStreaming) return
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submit()
    }
  }

  return (
    <div
      className="border-t border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-[#131313]"
      // Capacitor safe-area bottom inset so the composer clears the home
      // indicator (same isNative gating as navigation/sidebar.tsx).
      style={isNative ? { paddingBottom: 'calc(0.75rem + var(--safe-area-bottom))' } : undefined}
    >
      <div className="flex items-end gap-2">
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isStreaming ? 'Waiting for response…' : 'Ask anything…'}
          rows={2}
          disabled={isStreaming}
          className="flex-1 resize-none rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
        />
        {isStreaming ? (
          <button
            type="button"
            onClick={onStop}
            disabled={!onStop}
            aria-label="Stop generating"
            className="inline-flex items-center gap-1 rounded-md bg-neutral-800 px-2.5 py-2 text-xs font-medium text-white transition-colors hover:bg-neutral-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-neutral-200 dark:text-neutral-900 dark:hover:bg-neutral-100"
          >
            <Square className="h-3.5 w-3.5 fill-current" />
            Stop
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={!value.trim()}
            aria-label="Send message"
            className="rounded-md bg-blue-600 p-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
          </button>
        )}
      </div>
      <p className="mt-1 text-[11px] text-neutral-500 dark:text-neutral-400">
        {isStreaming
          ? 'Click Stop to cancel.'
          : 'Press Enter to send, Shift+Enter for a new line.'}
      </p>
    </div>
  )
}
